import React from 'react';
import { useInRouterContext, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, ProgressBar } from 'react-bootstrap';
import logo from '../../assets/GreatChildLogo-transparent.webp';

type Props = {
  redirectSeconds?: number;
  showDetails?: boolean;
  error?: Error | string | null;
  logoSrc?: string;
  onReset?: () => void;
};

export const brand = {
  white: '#FFFFFF',
  red: '#D62828',
  yellow: '#F7B801',
  blue: '#1D4ED8',
  green: '#22C55E',
  teal:'#008080',
} as const;

/** Generic countdown hook (no router hooks here). Calls onFinish immediately if seconds <= 0. */
function useCountdown(seconds: number, onFinish: () => void): number {
  const [left, setLeft] = React.useState<number>(seconds);

  React.useEffect(() => {
    if (seconds <= 0) {
      setLeft(0);
      onFinish();
      return;
    }
    setLeft(seconds);
    const t = window.setInterval(() => {
      setLeft((s) => {
        if (s <= 1) {
          window.clearInterval(t);
          onFinish();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => window.clearInterval(t);
  }, [seconds, onFinish]);

  return left;
}

/** Presentational UI shared by both variants. */
const FallbackUI: React.FC<
  Props & { secondsLeft: number; percent: number; onHome: () => void }
> = ({
  redirectSeconds = 5,
  showDetails = false,
  error,
  logoSrc = logo,
  onReset,
  secondsLeft,
  percent,
  onHome,
}) => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      background: `linear-gradient(135deg, ${brand.white} 0%, ${brand.yellow} 35%, ${brand.red} 65%, ${brand.blue} 85%, ${brand.green} 100%)`,
      backgroundAttachment: 'fixed',
    }}
  >
    <Container>
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <div className="shadow-lg rounded-4 p-4" style={{ background: brand.white, border: `2px solid ${brand.yellow}` }}>
            <div className="text-center mb-3">
              <img src={logoSrc} alt="Brand logo" style={{ height: 56, objectFit: 'contain' }} loading="lazy" decoding="async" fetchPriority="low" width={865} height={288} />
            </div>

            <div className="text-center">
              <h1 className="h3 mb-2" style={{ color: brand.red }}>Oops! Something went wrong</h1>
              <p className="text-muted mb-3">
                Don’t worry—we’re on it. You’ll be whisked back to the home page in a moment.
              </p>
            </div>

            {redirectSeconds > 0 && (
              <div className="mb-3">
                <ProgressBar
                  now={percent}
                  animated
                  striped
                  aria-label="Redirect progress"
                  style={{ height: 10, border: `1px solid ${brand.blue}` }}
                />
                <div className="text-end small text-muted mt-1">
                  Redirecting in <strong>{secondsLeft}s</strong>
                </div>
              </div>
            )}

            {showDetails && error && (
              <div className="bg-light rounded p-3 mb-3" style={{ border: `1px dashed ${brand.green}` }}>
                <div className="small text-muted mb-1">Details (for debugging)</div>
                <code className="small d-block" style={{ whiteSpace: 'pre-wrap' }}>
                  {typeof error === 'string' ? error : error.message}
                </code>
              </div>
            )}

            <div className="d-flex gap-2 justify-content-center">
              <Button variant="outline-danger" onClick={() => window.location.reload()}>
                Try again
              </Button>
              <Button style={{ backgroundColor: brand.blue, borderColor: brand.blue }} onClick={onHome}>
                Go home now
              </Button>
              {onReset && (
                <Button variant="outline-secondary" onClick={onReset}>
                  Reset
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  </div>
);

/** Routed variant – uses navigate('/', { replace: true }) so the error page isn’t left in history. */
const ErrorFallbackRouted: React.FC<Props> = (props) => {
  const navigate = useNavigate();
  const goHome = React.useCallback(() => navigate('/', { replace: true }), [navigate]);

  const total = props.redirectSeconds ?? 5;
  const secondsLeft = useCountdown(total, goHome);
  const percent = total > 0 ? Math.min(100, Math.round(((total - secondsLeft) / total) * 100)) : 100;

  return <FallbackUI {...props} secondsLeft={secondsLeft} percent={percent} onHome={goHome} />;
};

/** Non-routed variant – uses window.location.replace('/') for the same “no back to error” effect. */
const ErrorFallbackBare: React.FC<Props> = (props) => {
  const goHome = React.useCallback(() => window.location.replace('/'), []);
  const total = props.redirectSeconds ?? 5;
  const secondsLeft = useCountdown(total, goHome);
  const percent = total > 0 ? Math.min(100, Math.round(((total - secondsLeft) / total) * 100)) : 100;

  return <FallbackUI {...props} secondsLeft={secondsLeft} percent={percent} onHome={goHome} />;
};

/** Entry – picks variant by context; no conditional hook calls. */
const ErrorFallback: React.FC<Props> = (props) => {
  const inRouter = useInRouterContext();
  return inRouter ? <ErrorFallbackRouted {...props} /> : <ErrorFallbackBare {...props} />;
};

export default ErrorFallback;
